import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PERMISSIONS } from "@/constants/permissions"
import {
  getApiErrorCode,
  toastApiError,
} from "@/features/material-compliance/api/apiError"
import {
  DEFAULT_TEMPLATE_KEY,
  TEMPLATE_LANGS,
  useArchiveDeclarationTemplate,
  useCreateDeclarationTemplate,
  useDeclarationTemplates,
  useUpdateDeclarationTemplate,
  type TemplateCard,
} from "@/features/material-compliance/api/declarationTemplateApi"
import { dateFnsLocale } from "@/features/material-compliance/utils/date"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { format } from "date-fns"
import { Archive, Pencil, Plus, Star } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router"
import {
  SettingsCard,
  SettingsCardGrid,
  SettingsCardMenu,
} from "../../components/SettingsCard"
import { SettingsField, SettingsSection } from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"
import { useConfirmLeave } from "../../components/UnsavedChanges"
import { declarationTemplateEditorPath } from "../../paths"

/** "v2 · published 30 Sep 2026 · Jane Doe". The publisher shows only when
 *  there is a name to show: older versions carry just the user's id. */
function useVersionLine(card: TemplateCard): string {
  const { translate, currentLanguage } = useTranslation()
  const { user } = useUser()
  const current = card.currentVersion
  // Nothing published: the built-in layout is ready as it is, while a
  // template of the org's own can't be picked for a declaration yet.
  if (!current)
    return translate(
      card.key === DEFAULT_TEMPLATE_KEY
        ? "MC_DECL_TPL_CARD_BUILT_IN"
        : "MC_DECL_TPL_CARD_NEVER_PUBLISHED",
    )

  const date = format(new Date(current.publishedAt), "dd MMM yyyy", {
    locale: dateFnsLocale(currentLanguage),
  })
  const publishedBy = current.publishedBy?.toLowerCase()
  const byMe =
    !!publishedBy &&
    [user?.id, user?.email, user?.username].some(
      (id) => id?.toLowerCase() === publishedBy,
    )
  const who = current.publishedByName ?? (byMe ? translate("TEAM/YOU") : null)
  return who
    ? translate("MC_DECL_TPL_CARD_VERSION", {
        version: current.version,
        date,
        who,
      })
    : translate("MC_DECL_TPL_CARD_VERSION_NO_AUTHOR", {
        version: current.version,
        date,
      })
}

/** Asks for a template name, for a new template or a rename. */
function TemplateNameDialog({
  open,
  title,
  confirmLabel,
  initialName,
  pending,
  onOpenChange,
  onConfirm,
  children,
}: {
  open: boolean
  title: string
  confirmLabel: string
  initialName: string
  pending: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (name: string) => void
  children?: React.ReactNode
}) {
  const { translate } = useTranslation()
  const [name, setName] = useState(initialName)

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim()) onConfirm(name.trim())
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <SettingsField
              htmlFor="settings-template-name"
              label={translate("MC_DECL_TPL_NEW_NAME")}
            >
              <Input
                id="settings-template-name"
                value={name}
                maxLength={120}
                autoFocus
                onChange={(event) => setName(event.target.value)}
              />
            </SettingsField>
            {children}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              {translate("MC_MD_EDIT_CANCEL")}
            </Button>
            <PendingButton
              type="submit"
              pending={pending}
              disabled={!name.trim()}
            >
              {confirmLabel}
            </PendingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** The "Copy layout from" choice for a blank page (sent as `sourceKey:
 *  null`); template keys are slugs, so it can never collide with one. */
const BLANK_SOURCE = "__blank__"

function NewTemplateDialog({
  cards,
  onOpenChange,
}: {
  cards: TemplateCard[]
  onOpenChange: (open: boolean) => void
}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  // The editor is its own page: unsaved edits elsewhere on this one ask first.
  const confirmLeave = useConfirmLeave()
  const create = useCreateDeclarationTemplate()
  const [sourceKey, setSourceKey] = useState(
    cards.find((card) => card.isDefault)?.key ?? DEFAULT_TEMPLATE_KEY,
  )

  return (
    <TemplateNameDialog
      open
      title={translate("MC_DECL_TPL_NEW_TITLE")}
      confirmLabel={translate("MC_DECL_TPL_NEW_CONFIRM")}
      initialName=""
      pending={create.isPending}
      onOpenChange={onOpenChange}
      onConfirm={(name) =>
        create.mutate(
          {
            name,
            sourceKey: sourceKey === BLANK_SOURCE ? null : sourceKey,
          },
          {
            onSuccess: (card) => {
              toast.success(translate("MC_DECL_TPL_NEW_TOAST_OK"))
              onOpenChange(false)
              confirmLeave(() =>
                navigate(declarationTemplateEditorPath(card.key)),
              )
            },
            onError: (error) =>
              toastApiError(error, translate, "MC_DECL_TPL_NEW_TOAST_ERR"),
          },
        )
      }
    >
      <SettingsField
        htmlFor="settings-template-source"
        label={translate("MC_DECL_TPL_NEW_SOURCE")}
      >
        <Select value={sourceKey} onValueChange={setSourceKey}>
          <SelectTrigger id="settings-template-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BLANK_SOURCE}>
              {translate("MC_DECL_TPL_NEW_SOURCE_BLANK")}
            </SelectItem>
            {cards.map((card) => (
              <SelectItem key={card.key} value={card.key}>
                {card.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingsField>
    </TemplateNameDialog>
  )
}

function TemplateCardItem({
  card,
  canEdit,
}: {
  card: TemplateCard
  canEdit: boolean
}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  // The editor is its own page: unsaved edits elsewhere on this one ask first.
  const confirmLeave = useConfirmLeave()
  const update = useUpdateDeclarationTemplate()
  const archive = useArchiveDeclarationTemplate()
  const [renaming, setRenaming] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const versionLine = useVersionLine(card)
  const hasDraft = TEMPLATE_LANGS.some((lang) => card.unpublishedChanges[lang])

  const onUpdateError = (error: unknown) =>
    toastApiError(error, translate, "MC_DECL_TPL_UPDATE_TOAST_ERR")

  function makeDefault() {
    update.mutate(
      { key: card.key, isDefault: true },
      {
        onSuccess: () =>
          toast.success(
            translate("MC_DECL_TPL_DEFAULT_TOAST_OK", { name: card.name }),
          ),
        onError: onUpdateError,
      },
    )
  }

  function rename(name: string) {
    update.mutate(
      { key: card.key, name },
      {
        onSuccess: () => {
          setRenaming(false)
          toast.success(translate("MC_DECL_TPL_RENAME_TOAST_OK"))
        },
        onError: onUpdateError,
      },
    )
  }

  function onArchive() {
    archive.mutate(card.key, {
      onSuccess: () => {
        setArchiving(false)
        toast.success(
          translate("MC_DECL_TPL_ARCHIVE_TOAST_OK", { name: card.name }),
        )
      },
      onError: (error) => {
        if (getApiErrorCode(error) === "IS_DEFAULT") {
          toast.error(translate("MC_DECL_TPL_ARCHIVE_IS_DEFAULT"))
          return
        }
        onUpdateError(error)
      },
    })
  }

  return (
    <>
      <SettingsCard
        title={card.name}
        meta={versionLine}
        badges={
          <>
            {card.isDefault && (
              <Badge>{translate("MC_DECL_TPL_CARD_DEFAULT")}</Badge>
            )}
            {hasDraft && (
              <Badge variant="outline">
                {translate("MC_DECL_TPL_CARD_DRAFT_BADGE")}
              </Badge>
            )}
          </>
        }
        menu={
          canEdit && (
            <SettingsCardMenu
              label={translate("MC_DECL_TPL_CARD_MENU")}
              // "Set as default" runs straight from the menu.
              pending={update.isPending && !renaming}
              items={[
                {
                  label: translate("MC_DECL_TPL_SET_DEFAULT"),
                  icon: Star,
                  disabled: card.isDefault,
                  onSelect: makeDefault,
                },
                {
                  label: translate("MC_DECL_TPL_RENAME"),
                  icon: Pencil,
                  onSelect: () => setRenaming(true),
                },
                {
                  label: translate("MC_DECL_TPL_ARCHIVE"),
                  icon: Archive,
                  disabled: card.isDefault,
                  destructive: true,
                  onSelect: () => setArchiving(true),
                },
              ]}
            />
          )
        }
        footer={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              confirmLeave(() =>
                navigate(declarationTemplateEditorPath(card.key)),
              )
            }
          >
            <Pencil />
            {translate("MC_DECL_TPL_CARD_EDIT")}
          </Button>
        }
      />

      {renaming && (
        <TemplateNameDialog
          open
          title={translate("MC_DECL_TPL_RENAME_TITLE")}
          confirmLabel={translate("MC_DECL_TPL_RENAME")}
          initialName={card.name}
          pending={update.isPending}
          onOpenChange={setRenaming}
          onConfirm={rename}
        />
      )}

      <AlertDialog
        open={archiving}
        onOpenChange={(open) => !archive.isPending && setArchiving(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate("MC_DECL_TPL_ARCHIVE_TITLE", { name: card.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate("MC_DECL_TPL_ARCHIVE_BODY")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archive.isPending}>
              {translate("MC_MD_EDIT_CANCEL")}
            </AlertDialogCancel>
            <PendingButton
              variant="destructive"
              pending={archive.isPending}
              onClick={onArchive}
            >
              {translate("MC_DECL_TPL_ARCHIVE_CONFIRM")}
            </PendingButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/** The org's declaration templates: one card per live template, the
 *  built-in layout always among them. */
export function DeclarationTemplatesSection() {
  const { translate } = useTranslation()
  const { hasPermission } = useUser()
  const canEdit = hasPermission(PERMISSIONS.MC_CUSTOMER_DECLARATIONS_RELEASE)
  const templates = useDeclarationTemplates()
  const [creating, setCreating] = useState(false)

  return (
    <SettingsSection
      title={translate("MC_DECL_TPL_TITLE")}
      description={translate("MC_DECL_TPL_SUBTITLE")}
      action={
        canEdit &&
        templates.data && (
          <Button onClick={() => setCreating(true)}>
            <Plus />
            {translate("MC_DECL_TPL_NEW")}
          </Button>
        )
      }
    >
      {templates.data ? (
        <SettingsCardGrid>
          {templates.data.map((card) => (
            <TemplateCardItem key={card.key} card={card} canEdit={canEdit} />
          ))}
        </SettingsCardGrid>
      ) : templates.isLoading ? (
        <SettingsSkeleton rows={2} />
      ) : (
        <SettingsLoadError
          pending={templates.isFetching}
          onRetry={() => void templates.refetch()}
        />
      )}
      {creating && templates.data && (
        <NewTemplateDialog cards={templates.data} onOpenChange={setCreating} />
      )}
    </SettingsSection>
  )
}
